import { Box, Typography, Stack, Divider } from '@mui/material';
import UserReviewItemHeader from './UserReviewItemHeader';
import { useLocation, useParams } from 'react-router-dom';
import { saveCurrentPathAndRedirectToLoginWithScroll } from '../../../../utils/authUtils';
import { useNavigateWithScrollMemory } from '../../../../hooks/useNavigateWithScrollMemory';
import { useUserStore } from '../../../../stores/userStore';
import { useToggleReviewLike } from '../../hooks/useAlbumReviews';
import FavoriteButton from '../../../../components/button/FavoriteButton';
import { trackContentLike, trackReviewContentClick } from '../../../../lib/gaEvents';
import { CONTENT_TYPE, REVIEW_SECTION } from '../../../../constants/gaEnums';
import { getPageTypeFromPath } from '../../../../lib/gaParams';
import CommentReplyButton from '../../../../components/button/CommentReplyButton';
import UserWithProfile from '../../../../components/common/UserWithProfile';
import RatingReadOnly from '../../../../components/common/RatingReadOnly';
import theme from '../../../../theme';

const UserReviewItem = ({
  review,
  variant = 'default',
  showComments = true,
  dateMt,
  dateMb,
  containerPt,
  containerPb,
  albumSource: albumSourceProp,
  sourceId: sourceIdProp,
}) => {
  const params = useParams();
  const albumSource = albumSourceProp ?? params.albumSource;
  const sourceId = sourceIdProp ?? params.sourceId;
  const toggleLike = useToggleReviewLike(albumSource, sourceId);
  const location = useLocation();
  const navigateWithScrollMemory = useNavigateWithScrollMemory();
  const isLogin = useUserStore((state) => state.isLogin);
  const user = useUserStore((state) => state.user);
  const pageType = getPageTypeFromPath(location.pathname);

  const comments = review.comments || [];

  const handleLikeToggle = async () => {
    if (!isLogin) {
      saveCurrentPathAndRedirectToLoginWithScroll(navigateWithScrollMemory);
      return;
    }
    try {
      await toggleLike.mutateAsync({ reviewId: review.id, isLiked: review.isLiked });
      if (!review.isLiked) {
        trackContentLike(
          { contentType: CONTENT_TYPE.REVIEW, contentId: review.id, pageType },
          user,
        );
      }
    } catch (err) {
      console.error('Failed to toggle like:', err);
    }
  };

  const navigateToDetail = () => {
    if (!showComments) return;
    trackReviewContentClick(
      {
        section: REVIEW_SECTION.USER_REVIEWS,
        contentType: CONTENT_TYPE.REVIEW,
        contentId: review.id,
      },
      user,
    );
    const basePath = location.pathname.endsWith('/user-reviews')
      ? location.pathname
      : `${location.pathname}/user-reviews`;
    navigateWithScrollMemory(`${basePath}/${review.id}`);
  };

  return (
    <Box
      id={`review-${review.id}`}
      pt={containerPt ?? { xs: 1.5, md: 2, lg: 1.5 }}
      pb={containerPb ?? { xs: 1.5, md: 2, lg: 1.5 }}
    >
      {variant === 'profile' ? (
        <Stack direction="row" spacing={1} alignItems="center" mb={{ xs: 2, md: 1.5, lg: 2 }}>
          <UserWithProfile
            username={review.user.nickname}
            profileImageUrl={review.user.profileImageUrl}
            userNickname={review.user.nickname}
            grade={review.user.grade}
            onClick={() =>
              navigateWithScrollMemory(`/users/${encodeURIComponent(review.user.nickname)}`)
            }
            variant="small"
          />
          <Divider
            orientation="vertical"
            flexItem
            sx={{ height: '8px', alignSelf: 'center', backgroundColor: theme.palette.grey.line }}
          />
          <RatingReadOnly value={review.rating} variant="fullResponsive" />
        </Stack>
      ) : (
        <UserReviewItemHeader review={review} albumSource={albumSource} sourceId={sourceId} />
      )}

      {/* 리뷰 내용 */}
      <Box sx={{ mb: 2, cursor: showComments ? 'pointer' : 'default' }} onClick={navigateToDetail}>
        <Typography
          fontSize={14}
          color={theme.palette.black[6]}
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            maxWidth: '100%',
            lineHeight: 1.5,
          }}
        >
          {review.content}
        </Typography>
      </Box>

      {/* 수정일 / 좋아요 / 댓글 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mt: dateMt,
          mb: dateMb,
        }}
      >
        <Typography fontSize={14} color={theme.palette.black[1]}>
          {review.updatedAt}
        </Typography>
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            height: '8px',
            alignSelf: 'center',
            backgroundColor: theme.palette.grey.line,
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FavoriteButton
            isLiked={review.isLiked}
            onToggle={handleLikeToggle}
            likeCount={review.likeCount}
          />
          {showComments && (
            <CommentReplyButton count={comments.length} onClick={navigateToDetail} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default UserReviewItem;
